/**
 * «منشی هوشمند پارسینا» — the record button (PM-0080). Press: record. Press again:
 * stop, and the command is transcribed and run in the BACKGROUND while the desk
 * carries on. What comes back waits in `items` — shown as notifications — until
 * the desk opens it or dismisses it: a result has already cost پارسه, so it never
 * leaves on a timer. Opening one is SecretaryHost's business (model.routeOf).
 *
 * ── TWO MICROPHONES ──────────────────────────────────────────────────────────
 * A command's final transcript arrives after the press that ended it, and the
 * next command may be recorded meanwhile. Each stream (useServerVoiceInput) holds
 * one recording at a time, so there are two, and a new recording takes whichever
 * is free.
 *
 * ── WHAT IS NEVER SPENT ──────────────────────────────────────────────────────
 * A balance known to be too small never starts the microphone. A recording
 * thrown away (× or Esc) is cancelled without «done», so it is never transcribed.
 * Nothing heard is no command: no result, no notification — the button says so.
 * And a command is one sentence: a recording left running stops itself after
 * RECORD_LIMIT_S, and is sent.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bffMutation } from '../lib/bffClient';
import { useCredits } from '../hooks/useCredits';
import { useServerVoiceInput } from '../hooks/useServerVoiceInput';
import { RUN_SECRETARY_COMMAND, cannotAfford, isCreditRefusal } from './model';

export const RECORD_LIMIT_S = 60;
/** The last seconds of the limit, when the button counts down instead of up. */
export const COUNTDOWN_S = 10;
/** How long the button says «چیزی نشنیدم» (or why the microphone would not open). */
export const WARN_MS = 3500;

const MIC_FAILURES = new Set(['denied', 'unsupported', 'error']);

let sequence = 0;
const nextId = () => {
  sequence += 1;
  return `cmd-${Date.now().toString(36)}-${sequence}`;
};

export function useSecretaryController() {
  // The meter's readings go straight to whoever draws them — never through this
  // hook's state, which would re-render the whole shell ten times a second.
  const levelListeners = useRef(new Set());
  const onLevel = useCallback((level) => { levelListeners.current.forEach((fn) => fn(level)); }, []);
  const voiceA = useServerVoiceInput({ onLevel });
  const voiceB = useServerVoiceInput({ onLevel });
  const credits = useCredits();
  const voices = useRef({});
  voices.current = { a: voiceA, b: voiceB };
  const creditsRef = useRef(credits);
  creditsRef.current = credits;

  // The stream recording now — 'a' | 'b' | null. A ref as well: presses arrive
  // from keys and clicks between renders.
  const [slot, setSlotState] = useState(null);
  const slotRef = useRef(null);
  const setSlot = useCallback((next) => { slotRef.current = next; setSlotState(next); }, []);
  // A stream is busy from its start until its final transcript is in.
  const busy = useRef({ a: false, b: false });
  const [seconds, setSeconds] = useState(0);
  // Commands between «stop» and their result: transcribing, then running.
  const [pending, setPending] = useState(0);
  const [warn, setWarn] = useState(null);
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const flash = useCallback((kind, detail = null) => setWarn({ kind, detail, at: Date.now() }), []);
  useEffect(() => {
    if (!warn) return undefined;
    const id = setTimeout(() => setWarn(null), WARN_MS);
    return () => clearTimeout(id);
  }, [warn]);

  const status = slot ? voices.current[slot].status : null;
  const live = status === 'listening';
  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [live]);

  const addItem = useCallback((item) => setItems((list) => [...list, item]), []);
  const update = useCallback((id, patch) => {
    setItems((list) => list.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);
  const dismiss = useCallback((id) => {
    setItems((list) => list.filter((item) => item.id !== id));
    setOpenId((current) => (current === id ? null : current));
  }, []);

  const run = useCallback(async (id, text) => {
    setPending((n) => n + 1);
    try {
      const data = await bffMutation(RUN_SECRETARY_COMMAND, {
        input: { transcript: text, patientId: null, doctorId: null, newPatient: false, questions: true },
      });
      if (!alive.current) return;
      update(id, { status: 'ready', result: data?.runSecretaryCommand || { outcome: 'UNKNOWN', charged: false } });
    } catch (err) {
      if (!alive.current) return;
      update(id, { status: 'ready', error: isCreditRefusal(err) ? 'credit' : 'failed' });
    } finally {
      if (alive.current) setPending((n) => n - 1);
      // The balance in the sidebar and the next «can I afford it» both read this.
      creditsRef.current.refresh?.();
    }
  }, [update]);

  const start = useCallback(() => {
    if (slotRef.current) return;
    const { balance, priceOf } = creditsRef.current;
    if (cannotAfford(balance, priceOf)) {
      addItem({ id: nextId(), createdAt: Date.now(), status: 'ready', text: '', result: null, error: 'credit' });
      return;
    }
    const free = !busy.current.a ? 'a' : !busy.current.b ? 'b' : null;
    if (!free) { flash('busy'); return; }
    busy.current[free] = true;
    setWarn(null);
    setSeconds(0);
    setSlot(free);
    const voice = voices.current[free];
    // A stream that failed before keeps its failure until it is cleared.
    voice.cancel();
    voice.start({ meter: true });
  }, [addItem, flash, setSlot]);

  const stop = useCallback(async () => {
    const current = slotRef.current;
    if (!current) return;
    const voice = voices.current[current];
    setSlot(null);
    if (voice.status !== 'listening') {
      // Pressed again before the stream was up: nothing was recorded, nothing is sent.
      voice.cancel();
      busy.current[current] = false;
      return;
    }
    setPending((n) => n + 1);
    const text = String((await voice.finishTranscript()) || '').trim();
    busy.current[current] = false;
    if (!alive.current) return;
    setPending((n) => n - 1);
    if (!text) { flash('nothing'); return; }
    const id = nextId();
    addItem({ id, createdAt: Date.now(), status: 'processing', text, result: null, error: null });
    run(id, text);
  }, [addItem, flash, run, setSlot]);

  /** × or Esc — throw the recording away: closed without «done», never transcribed. */
  const discard = useCallback(() => {
    const current = slotRef.current;
    if (!current) return;
    voices.current[current].cancel();
    busy.current[current] = false;
    setSlot(null);
  }, [setSlot]);

  const toggle = useCallback(() => {
    if (slotRef.current) stop();
    else start();
  }, [start, stop]);

  // The microphone refused: say why on the button, and record nothing.
  useEffect(() => {
    if (!slot || !MIC_FAILURES.has(status)) return;
    busy.current[slot] = false;
    setSlot(null);
    flash('mic', status);
  }, [slot, status, flash, setSlot]);

  // A command is one sentence: a recording left running stops itself, and is sent.
  useEffect(() => {
    if (slot && seconds >= RECORD_LIMIT_S) stop();
  }, [slot, seconds, stop]);

  const subscribeLevel = useCallback((fn) => {
    levelListeners.current.add(fn);
    return () => { levelListeners.current.delete(fn); };
  }, []);

  // Only what came back is shown; what is still on its way shows on the button.
  const ready = useMemo(() => items.filter((item) => item.status === 'ready' && item.id !== openId), [items, openId]);
  const openItem = useMemo(() => items.find((item) => item.id === openId) || null, [items, openId]);

  return {
    recording: Boolean(slot),
    live,
    seconds,
    remaining: Math.max(0, RECORD_LIMIT_S - seconds),
    pending,
    warn,
    items: ready,
    openItem,
    start,
    stop,
    discard,
    toggle,
    open: setOpenId,
    close: useCallback(() => setOpenId(null), []),
    update,
    dismiss,
    subscribeLevel,
  };
}

export default useSecretaryController;
